import {
  Box,
  Button,
  FormControl,
  FormLabel,
  Input,
  VStack,
  useToast,
  Text,
  FormHelperText,
  InputGroup,
  InputRightElement,
  IconButton,
} from "@chakra-ui/react";
import { ViewIcon, ViewOffIcon } from "@chakra-ui/icons";
import React, { useState } from "react";

interface TwilioSettingsProps {
  communityId: string;
  currentPhoneNumber?: string;
}

export default function TwilioSettings({
  communityId,
  currentPhoneNumber,
}: TwilioSettingsProps) {
  const [twilioAccountSid, setTwilioAccountSid] = useState("");
  const [twilioAuthToken, setTwilioAuthToken] = useState("");
  const [phoneNumber, setPhoneNumber] = useState(currentPhoneNumber || "");
  const [isLoading, setIsLoading] = useState(false);
  const [showSid, setShowSid] = useState(false);
  const [showToken, setShowToken] = useState(false);
  const toast = useToast();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Validation
    if (!twilioAccountSid || !twilioAuthToken || !phoneNumber) {
      toast({
        title: "Missing fields",
        description: "Please fill in all fields",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    if (!phoneNumber.startsWith("+")) {
      toast({
        title: "Invalid phone number",
        description: "Phone number must start with + (E.164 format)",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    setIsLoading(true);

    try {
      // Get API base URL from environment or use default
      const apiBaseUrl =
        process.env.REACT_APP_API_BASE_URL ||
        "https://us-central1-intronet-88bc5.cloudfunctions.net";

      const response = await fetch(
        `${apiBaseUrl}/api/updateCommunityTwilioCredentials`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            communityId,
            twilioAccountSid,
            twilioAuthToken,
            phoneNumber,
          }),
        }
      );

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || "Failed to update credentials");
      }

      toast({
        title: "Success",
        description: "Twilio credentials updated successfully",
        status: "success",
        duration: 5000,
        isClosable: true,
      });

      // Clear sensitive fields after successful update
      setTwilioAccountSid("");
      setTwilioAuthToken("");
    } catch (error) {
      toast({
        title: "Error",
        description:
          error instanceof Error
            ? error.message
            : "Failed to update credentials",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Box
      p={6}
      borderWidth="1px"
      borderRadius="lg"
      bg="white"
      shadow="sm"
      maxW="600px"
    >
      <Text fontSize="xl" fontWeight="bold" mb={4}>
        WhatsApp / Twilio Settings
      </Text>
      <Text fontSize="sm" color="gray.600" mb={6}>
        Configure Twilio credentials for WhatsApp messaging. These credentials
        will be encrypted and stored securely.
      </Text>

      <form onSubmit={handleSubmit}>
        <VStack spacing={4} align="stretch">
          <FormControl isRequired>
            <FormLabel>WhatsApp Phone Number</FormLabel>
            <Input
              type="tel"
              placeholder="+14155551234"
              value={phoneNumber}
              onChange={(e) => setPhoneNumber(e.target.value)}
            />
            <FormHelperText>
              Must be in E.164 format (e.g., +14155551234)
            </FormHelperText>
          </FormControl>

          <FormControl isRequired>
            <FormLabel>Twilio Account SID</FormLabel>
            <InputGroup>
              <Input
                type={showSid ? "text" : "password"}
                placeholder="AC..."
                value={twilioAccountSid}
                onChange={(e) => setTwilioAccountSid(e.target.value)}
              />
              <InputRightElement>
                <IconButton
                  aria-label={showSid ? "Hide SID" : "Show SID"}
                  icon={showSid ? <ViewOffIcon /> : <ViewIcon />}
                  onClick={() => setShowSid(!showSid)}
                  variant="ghost"
                  size="sm"
                />
              </InputRightElement>
            </InputGroup>
            <FormHelperText>
              Find this in your Twilio Console
            </FormHelperText>
          </FormControl>

          <FormControl isRequired>
            <FormLabel>Twilio Auth Token</FormLabel>
            <InputGroup>
              <Input
                type={showToken ? "text" : "password"}
                placeholder="••••••••"
                value={twilioAuthToken}
                onChange={(e) => setTwilioAuthToken(e.target.value)}
              />
              <InputRightElement>
                <IconButton
                  aria-label={showToken ? "Hide token" : "Show token"}
                  icon={showToken ? <ViewOffIcon /> : <ViewIcon />}
                  onClick={() => setShowToken(!showToken)}
                  variant="ghost"
                  size="sm"
                />
              </InputRightElement>
            </InputGroup>
            <FormHelperText>
              Find this in your Twilio Console
            </FormHelperText>
          </FormControl>

          <Button
            type="submit"
            colorScheme="blue"
            isLoading={isLoading}
            loadingText="Updating..."
            mt={2}
          >
            Update Credentials
          </Button>
        </VStack>
      </form>

      {currentPhoneNumber && (
        <Box mt={4} p={3} bg="gray.50" borderRadius="md">
          <Text fontSize="sm" color="gray.600">
            Current WhatsApp Number:{" "}
            <Text as="span" fontWeight="bold" color="gray.800">
              {currentPhoneNumber}
            </Text>
          </Text>
        </Box>
      )}
    </Box>
  );
}
